import React, { Fragment, useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { ActionPanel } from "components/panel/ActionPanel";
import { GridLayout } from "components/grid";
import { ShowHide } from "components/layout";
import { Intent } from "@blueprintjs/core";
import { AlertConfirm } from "components/notifications/AlertConfirm";
import { ListingTable } from "components/listing";
import { NotificationToaster, NotificationInline } from "components/notifications";
import { Tooltip } from "components/tooltip";
import { BlobStorageService, FleetService } from "services";
import { Money } from "components/formatting/Money";
import { MotPanel } from "pages/asset/events/shared/MotPanel";
import { forEach } from "underscore";
import { Button } from "components/buttons";
import { EventChecksPanel } from "./EventChecksPanel";
import { UserHasAccess } from "components/user-access";
import { StringToEllipsesString } from "components/formatting";
import { StatusBadge } from "components/status";
import classNames from "classnames";
import { DateToLocal } from "components/dates";

export function EventPanel({
    event,
    eventType,
    eventOptions,
    onDelete,
    detailedChecks,
    loadingData = false,
    reopenFunction,
    openEditEventModal,
    matchedEventTypeForAsset,
    expanded,
    onEventCollapseToggle
}) {

    const editAction = "FMS:Edit";
    const canEditEvent = UserHasAccess(editAction);

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [allowDelete, setAllowDelete] = useState(false);
    const intentPrimary = "#008CFF"; // default intent primary
    const [motData, setMotData] = useState(<></>);

    const [costTableRows, setCostTableRows] = useState([]);
    const [defectTableRows, setDefectTableRows] = useState([]);
    const [costTotalRecords, setCostTotalRecords] = useState(0);

    const [fileDownloading, setFileDownloading] = useState(false);

    const [signoffNotificationContent, setSignoffNotificationContent] = useState(<></>);
    const [signoffSecondNotificationContent, setSignoffSecondNotificationContent] = useState(<></>);
    const [isSignedOff, setIsSignedOff] = useState(false);
    const [isSignedOffSecond, setIsSignedOffSecond] = useState(false);
    const [eventStatus, setEventStatus] = useState(null);

    const costTableHeaders = [
        "Cost",
        "Labour",
        "Parts",
        "Sundries",
        "Total",
        "Supplier"
    ];

    const defectTableHeaders = [
        "Name",
        "Im No.",
        "Comment",
        "Safety",
        "Staus",
        "Rectified Comment",
        "Maintenance Provider",
        "Rectified Details",
        "Signoff Details"
    ];

    const plannedStatusKey = "PLANNED";
    const defaultButtonModel = { show: false, text: "", action: () => { } };
    const [scheduleButtonModel, setScheduleButtonModel] = useState(defaultButtonModel);
    const inProgressStatusName = "In Progress";
    const inProgressFailedStatusName = "In Progress Failed";

    function showField(fieldName) {
        return event !== null && event !== undefined && event[fieldName] !== null && event[fieldName] !== undefined;
    }

    function getEventDateLabel() {
        switch (event?.statusName) {
            case "Scheduled":
                return "Scheduled Date";
            case "Planned":
                return "Planned Date";
            default:
                return "Event Date";
        }
    }

    function formatDate(date) {
        if (!date) {
            return "";
        }
        return moment(date).format("DD/MM/YYYY");
    }

    function shouldShowVorDates() {
        if (eventType?.vorTimerFailedOnly) {
            return eventStatus?.failedStatus;
        }

        return eventType?.vorTimer ?? false;
    }

    function getVorStartDate() {
        if (event?.vorStartDate) {
            return formatDate(event.vorStartDate);
        }
        if (eventType?.vorTimer && typeof eventType.vorPreDays === "number" && event?.eventDate) {
            const date = moment(event.eventDate).subtract(eventType.vorPreDays, "days");
            return formatDate(date);
        }
        return "";
    }

    function getVorEndDate() {
        if (event?.vorEndDate) {
            return formatDate(event.vorEndDate);
        }
        if (eventType?.vorTimer && typeof eventType.vorPostDays === "number" && event?.eventDate) {
            const date = moment(event.eventDate).add(eventType.vorPostDays, "days");
            return formatDate(date);
        }
        return "";
    }

    function getMotData() {
        if (event && event?.motDetails && event?.motDetails.length > 0) {

            let motdetails = [];

            forEach(event.motDetails, (m) => {
                motdetails.push(<MotPanel mot={m} eventOptions={eventOptions} />);
            });

            setMotData(motdetails);
        }
    }

    const totalCosts = useMemo(() => {
        if (!event?.costs || !Array.isArray(event.costs)) {
            return <Money>{0}</Money>;
        }
        const total = event.costs.reduce((sum, cost) => {
            const parts = Number(cost.parts) || 0;
            const labour = Number(cost.labour) || 0;
            const sundries = Number(cost.sundries) || 0;
            return sum + parts + labour + sundries;
        }, 0);
        return <Money>{total}</Money>;
    }, [event?.costs]);

    const outstandingRequirementsWarning = useMemo(() => {
        return (<Fragment>This {event?.name} event has the following outstanding requirements: <strong>{event?.outstandingRequirements?.join(", ")}</strong></Fragment>);
    }, [event?.name, event?.outstandingRequirements]);

    const eventHeading = useMemo(() => {
        if (event) {
            if (!expanded) {
                return `${event.name} - ${formatDate(event.eventDate)} - ${event.statusName}`;
            }
            return event.name;
        }
        return "";

    }, [event?.name, expanded]);


    const handleDelete = useCallback(() => {
        onDelete?.(event.eventId);
        setShowDeleteDialog(false);
    }, [onDelete, event?.eventId]);

    const isReopenHidden = scheduleButtonModel.text === "Reopen Event" && eventType?.hidden === true;

    useEffect(() => {
        if (!event || !eventType || !eventOptions?.eventStatuses) {
            return;
        }

        getMotData();

        let matchedStatus = eventOptions.eventStatuses.find(
            s => s.name.toLowerCase() === event.statusName?.toLowerCase()
        );

        setEventStatus(matchedStatus);

        const eventDefects = event?.defects || [];
        setDefectTableRows(mapDefectsToTableRows(eventDefects));
        const eventCosts = event?.costs || [];
        setCostTableRows(mapCostsToTableRows(eventCosts));
        setCostTotalRecords(eventCosts.length);

        let isEventSignedOff = (eventType.requiresSignoff || eventType.requiresSignoffFailedOnly) && event.signoffDate;
        if (isEventSignedOff) {
            setIsSignedOff(true);
            setSignoffNotificationContent(<Fragment>Event signed off by {event?.signoffUser} on <DateToLocal format='DD/MM/YYYY HH:mm'>{event?.signoffDate}</DateToLocal></Fragment>);
            if (event.signoffSecondDate) {
                setIsSignedOffSecond(true);
                setSignoffSecondNotificationContent(<Fragment>Event second signed off by {event?.signoffSecondUser} on <DateToLocal format='DD/MM/YYYY HH:mm'>{event?.signoffSecondDate}</DateToLocal></Fragment>);
            } else {
                setIsSignedOffSecond(false);
                setSignoffSecondNotificationContent(<></>);
            }
        } else {
            setIsSignedOff(false);
            setSignoffNotificationContent(<></>);
            setIsSignedOffSecond(false);
            setSignoffSecondNotificationContent(<></>);
        }

    }, [event, eventType, matchedEventTypeForAsset, eventOptions]);

    useEffect(() => {
        if (!eventStatus) {
            return;
        }

        let text = "";
        let action = () => { };

        if (eventStatus.completedStatus) {
            text = "Reopen Event";
            action = reopenFunction;
        } else {
            text = eventStatus.key === plannedStatusKey ? "Schedule Event" : "Edit Event";
            action = (e) => { openEditEventModal(e); };
        }

        setScheduleButtonModel(prev => {
            return {
                ...prev,
                show: canEditEvent,
                text: text,
                action: action
            };
        });

        if (!eventStatus.completedStatus && (eventType.adHoc || eventType.hidden || matchedEventTypeForAsset?.scheduled === false)) {
            setAllowDelete(true);
        } else {
            setAllowDelete(false);
        }

    }, [eventStatus]);

    function mapDefectsToTableRows(eventDefects) {
        if (!eventDefects || !Array.isArray(eventDefects) || eventDefects.length === 0) {
            return [];
        }

        return eventDefects.map(defect => {
            let defectStatus = "Open";
            let statusClass = "text-intent-primary";
            if (defect.rectified !== undefined && defect.rectified !== null) {
                if (defect.monitored === true) {
                    defectStatus = "Monitored";
                    statusClass = "text-intent-warning";
                } else {
                    defectStatus = defect.rectified ? "Rectified" : "Not Rectified";
                    statusClass = defect.rectified ? "text-intent-success" : "text-intent-danger";
                }
            }
            let defectRectitifed = !defect.rectifiedDate ? "-" : formatDate(defect.rectifiedDate) + " - " + defect.rectifiedBy;
            let defectSignoff = !defect.rectifiedDate ? "-" : formatDate(defect.signoffDate) + " - " + defect.signoffUser;

            return [
                <div key={`${defect.eventDefectId}-name`}><StringToEllipsesString length={20} showTooltip={true}>{defect.defectName}</StringToEllipsesString></div>,
                <div key={`${defect.eventDefectId}-im`}>{defect.defectIMNumber}</div>,
                <div key={`${defect.eventDefectId}-comment`}><StringToEllipsesString length={20} showTooltip={true}>{defect.defectComment}</StringToEllipsesString></div>,
                <div key={`${defect.eventDefectId}-safety`} className="nowrap">
                    <ShowHide
                        evaluator={defect.safety === true}
                        show={(
                            <StatusBadge
                                text="Safety"
                                iconName="warning-sign"
                                intent={Intent.DANGER}
                                showTooltip={true}
                                tooltipContent="This is a safety-critical defect."
                            />
                        )}
                    />
                </div>,
                <div key={`${defect.eventDefectId}-status`} className={statusClass}>{defectStatus}</div>,
                <div key={`${defect.eventDefectId}-rectified-comment`}><StringToEllipsesString length={20} showTooltip={true}>{defect.rectifiedComment}</StringToEllipsesString></div>,
                <div key={`${defect.eventDefectId}-maintenance-provider`}><StringToEllipsesString length={20} showTooltip={true}>{defect.maintenanceProvider}</StringToEllipsesString></div>,
                <div key={`${defect.eventDefectId}-rectified-by`}><StringToEllipsesString length={20} showTooltip={true}>{defectRectitifed}</StringToEllipsesString></div>,
                <div key={`${defect.eventDefectId}-signoff-by`}><StringToEllipsesString length={20} showTooltip={true}>{defectSignoff}</StringToEllipsesString></div>
            ];
        });
    }

    function mapCostsToTableRows(eventCosts) {
        const costRows = eventCosts.map(cost => {
            return [
                <div key={`${cost.eventCostId}-cost`}>{cost.type}</div>,
                <div key={`${cost.eventCostId}-labour`}><Money>{cost.labour ? Number(cost.labour).toFixed(2) : 0.00}</Money></div>,
                <div key={`${cost.eventCostId}-parts`}><Money>{cost.parts ? Number(cost.parts).toFixed(2) : 0.00}</Money></div>,
                <div key={`${cost.eventCostId}-sundries`}><Money>{cost.sundries ? Number(cost.sundries).toFixed(2) : 0.00}</Money></div>,
                <div key={`${cost.eventCostId}-total`}><Money>{(Number(cost.labour || 0) + Number(cost.parts || 0) + Number(cost.sundries || 0)).toFixed(2)}</Money></div>,
                <div key={`${cost.eventCostId}-supplier`}>{cost.supplierName}</div>
            ];
        });

        if (costRows.length > 0) {
            const totalLabour = eventCosts.reduce((sum, cost) => sum + (Number(cost.labour) || 0), 0);
            const totalParts = eventCosts.reduce((sum, cost) => sum + (Number(cost.parts) || 0), 0);
            const totalSundries = eventCosts.reduce((sum, cost) => sum + (Number(cost.sundries) || 0), 0);

            const costTotalsRow = [
                <div key={`total-cost`}>Totals</div>,
                <div key={`total-labour`}><Money>{totalLabour !== 0 ? Number(totalLabour).toFixed(2) : 0.00}</Money></div>,
                <div key={`total-parts`}><Money>{totalParts !== 0 ? Number(totalParts).toFixed(2) : 0.00}</Money></div>,
                <div key={`total-sundries`}><Money>{totalSundries !== 0 ? Number(totalSundries).toFixed(2) : 0.00}</Money></div>,
                <div key={`total-total`}><Money>{(totalLabour + totalParts + totalSundries).toFixed(2)}</Money></div>,
                <div key={`total-supplier`}></div>
            ];

            costRows.push(costTotalsRow);
        }

        return costRows;
    }

    function showComments() {
        return eventType !== null && eventType !== undefined && eventType.includeComment === true && event.comment !== null && event.comment !== undefined && event.comment.trim() !== "";
    }



    function showDefects() {
        return eventType !== null &&
            eventType !== undefined &&
            (eventType.includeDefect === true || eventType.includeDefectFailedOnly === true) &&
            event.defects !== null &&
            event.defects !== undefined &&
            event.defects.length > 0;
    }

    function showDocuments() {
        return eventType !== null &&
            eventType !== undefined &&
            (eventType.includeDocumentation === true || eventType.includeDocumentationFailedOnly === true) &&
            event.documents !== null &&
            event.documents !== undefined &&
            event.documents.length > 0;
    }

    function getDocuments() {
        return event?.documents !== null && event?.documents
            ? event.documents.map(doc => {
                return (
                    <Button
                        key={doc.eventDocumentationId}
                        intent="primary"
                        onClick={() => downloadDocument(doc.eventDocumentationId)}
                        text={doc.name}
                        disabled={fileDownloading}
                        minimal
                    />
                );
            })
            : [];
    }

    function downloadDocument(eventDocumentationId) {
        setFileDownloading(true);

        FleetService.getEventDocumentLink(event.eventId, eventDocumentationId)
            .then((response) => {
                NotificationToaster.show(Intent.SUCCESS, "Your file will be downloaded shortly", false);

                BlobStorageService.downloadFile(
                    `https://${response.storageName}.blob.core.windows.net`,
                    response.containerName,
                    response.fileName,
                    response.token,
                    response.downloadName
                )
                    .then(() => {
                        setFileDownloading(false);
                    })
                    .catch(() => {
                        throwGetFileError();
                    });
            })
            .catch(() => {
                throwGetFileError();
            });
    }

    function throwGetFileError() {
        setFileDownloading(false);
        NotificationToaster.show(Intent.DANGER, "Unable to download file", false);
    }

    function showCosts() {
        return eventType !== null &&
            eventType !== undefined &&
            eventType.includeCosts === true &&
            event.costs !== null &&
            event.costs !== undefined &&
            event.costs.length > 0;
    }

    function onClickExpanded() {
        if (onEventCollapseToggle) {
            onEventCollapseToggle();
        }
    }

    return (
        <Fragment>
            <div className="spacer-bottom">
                <ActionPanel
                    intent="info"
                    title={eventHeading}
                    colour={eventStatus?.statusColour || intentPrimary}
                    loadingData={loadingData}
                    collapsable={onEventCollapseToggle !== undefined}
                    expanded={expanded}
                    onClick={onClickExpanded}
                >
                    <NotificationInline
                        allowClose={false}
                        text={outstandingRequirementsWarning}
                        intent="info"
                        show={event?.outstandingRequirements && event.outstandingRequirements.length > 0
                            && (event.statusName === inProgressStatusName || event.statusName === inProgressFailedStatusName)}
                        icon={null}
                    />

                    <NotificationInline
                        allowClose={false}
                        text={signoffNotificationContent}
                        intent="success"
                        show={isSignedOff}
                        icon={null}
                    />

                    <NotificationInline
                        allowClose={false}
                        text={signoffSecondNotificationContent}
                        intent="success"
                        show={isSignedOffSecond}
                        icon={null}
                    />

                    <GridLayout numberOfColumns={5} className="form-group">
                        <div className="form-field">
                            <h4 className="summary-field-header">Event Status</h4>
                            <div>{event?.statusName}</div>
                        </div>
                        <ShowHide
                            evaluator={shouldShowVorDates()}
                            show={(
                                <div className="form-field">
                                    <h4 className="summary-field-header">VOR Start Date</h4>
                                    <div>{getVorStartDate()}</div>
                                </div>
                            )}
                        />
                        <div className="form-field">
                            <h4 className="summary-field-header">{getEventDateLabel()}</h4>
                            <div>{formatDate(event?.eventDate)}</div>
                        </div>
                        <ShowHide
                            evaluator={showField("completedDate")}
                            show={
                                <div className="form-field">
                                    <h4 className="summary-field-header">Completed Date</h4>
                                    <div>{formatDate(event?.completedDate)}</div>
                                </div>
                            }
                        />
                        <ShowHide
                            evaluator={showField("completedUser")}
                            show={
                                <div className="form-field">
                                    <h4 className="summary-field-header">Completed User</h4>
                                    <div>{event?.completedUser}</div>
                                </div>
                            }
                        />
                        <ShowHide
                            evaluator={shouldShowVorDates()}
                            show={(
                                <div className="form-field">
                                    <h4 className="summary-field-header">VOR End Date</h4>
                                    <div>{getVorEndDate()}</div>
                                </div>
                            )}
                        />
                        <div className="form-field">
                            <h4 className="summary-field-header">Total Costs</h4>
                            <div>{totalCosts}</div>
                        </div>
                        <ShowHide
                            evaluator={Array.isArray(event?.relatedEvents) && event?.relatedEvents.length > 0}
                            show={
                                <div className="form-field">
                                    <h4 className="summary-field-header">Associated Events</h4>
                                    <div>
                                        {Array.isArray(event?.relatedEvents) && event?.relatedEvents.length > 0 &&
                                            event.relatedEvents.map((related, idx) => (
                                                <div key={related.eventId || idx} style={{ marginBottom: "4px" }}>
                                                    <strong>{related.name}</strong>
                                                    {" - "}
                                                    <span>{related.eventStatus}</span>
                                                    {" - "}
                                                    {formatDate(related.eventDate)}
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            }
                        />
                        <ShowHide
                            evaluator={showField("signoffDate")}
                            show={
                                <div className="form-field">
                                    <h4 className="summary-field-header">Signoff Date</h4>
                                    <div>{formatDate(event?.signoffDate)}</div>
                                </div>
                            }
                        />
                        <ShowHide
                            evaluator={showField("signoffUser")}
                            show={
                                <div className="form-field">
                                    <h4 className="summary-field-header">Signoff User</h4>
                                    <div>{event?.signoffUser}</div>
                                </div>
                            }
                        />
                        <ShowHide
                            evaluator={showField("signoffSecondDate")}
                            show={
                                <div className="form-field">
                                    <h4 className="summary-field-header">Second Signoff Date</h4>
                                    <div>{formatDate(event?.signoffSecondDate)}</div>
                                </div>
                            }
                        />
                        <ShowHide
                            evaluator={showField("signoffSecondUser")}
                            show={
                                <div className="form-field">
                                    <h4 className="summary-field-header">Second Signoff User</h4>
                                    <div>{event?.signoffSecondUser}</div>
                                </div>
                            }
                        />
                    </GridLayout>

                    <ShowHide
                        evaluator={showComments()}
                        show={(
                            <GridLayout numberOfColumns={1}>
                                <div className="form-field">
                                    <h4 className="summary-field-header">Comment</h4>
                                    <div>{event?.comment}</div>
                                </div>
                            </GridLayout>
                        )}
                    />

                    <ShowHide
                        evaluator={showDefects()}
                        show={(
                            <div className="form-field">
                                <h4 className="summary-field-header">Defects</h4>
                                <ListingTable
                                    id="listing-table-fleet-event-defects"
                                    headers={defectTableHeaders}
                                    data={defectTableRows}
                                    pageable={false}
                                    loadingData={loadingData}
                                    totalRecordCount={defectTableRows ? defectTableRows.length : 0}
                                />
                            </div>
                        )}
                    />

                    <ShowHide
                        evaluator={showCosts()}
                        show={(
                            <div className="form-field">
                                <h4 className="summary-field-header">Full Cost Breakdown</h4>
                                <ListingTable
                                    id="listing-table-fleet-event-costs"
                                    tableClasses="pricing-table"
                                    headers={costTableHeaders}
                                    data={costTableRows}
                                    totalRecordCount={costTotalRecords}
                                    pageable={false}
                                    loadingData={loadingData}
                                />
                            </div>
                        )}
                    />

                    <ShowHide
                        evaluator={showDocuments()}
                        show={(
                            <div className="form-field">
                                <h4 className="summary-field-header">Documents</h4>
                                <div className="inline-items">{getDocuments()}</div>
                            </div>
                        )}
                    />

                    <ShowHide
                        evaluator={event?.checks && event.checks.length > 0}
                        show={(
                            <div className={classNames("spacer-bottom", { "bp6-skeleton": !detailedChecks?.length })}>
                                <ShowHide
                                    evaluator={detailedChecks && detailedChecks.length > 0}
                                    show={(
                                        Array.isArray(detailedChecks) && detailedChecks.length > 0 && (
                                            detailedChecks.map((check, idx) => (
                                                <div key={check.checkId || idx}>
                                                    <EventChecksPanel
                                                        check={check}
                                                    />
                                                </div>
                                            ))
                                        )
                                    )}
                                    hide={(
                                        <EventChecksPanel />
                                    )}
                                />
                            </div>
                        )}
                    />

                    <ShowHide
                        evaluator={showField("motDetails")}
                        show={motData}
                    />

                    <div className="inline-items inline-items-end button-row">
                        <ShowHide
                            evaluator={scheduleButtonModel.show}
                            show={(
                                <Tooltip
                                    content="Cannot reopen event because this event type is hidden"
                                    position="top"
                                    disabled={!isReopenHidden}
                                >
                                    <Button
                                        intent="primary"
                                        text={scheduleButtonModel.text}
                                        onClick={scheduleButtonModel.action}
                                        disabled={isReopenHidden}
                                    />
                                </Tooltip>
                            )}
                        />

                        <ShowHide
                            evaluator={canEditEvent === true && !eventStatus?.completedStatus}
                            show={(

                                <Button
                                    text="Delete"
                                    intent={Intent.DANGER}
                                    onClick={() => setShowDeleteDialog(true)}
                                    disabled={!allowDelete || !onDelete}
                                />
                            )}
                        />
                    </div>
                </ActionPanel>
            </div>

            <AlertConfirm
                title="Please confirm"
                isOpen={showDeleteDialog}
                onConfirm={handleDelete}
                onCancel={() => setShowDeleteDialog(false)}
            >
                <p>Are you sure you want to delete this event?</p>
            </AlertConfirm>
        </Fragment>
    );
}

EventPanel.propTypes = {
    event: PropTypes.object,
    eventType: PropTypes.object,
    eventOptions: PropTypes.object,
    onDelete: PropTypes.func.isRequired,
    detailedChecks: PropTypes.array,
    loadingData: PropTypes.bool,
    reopenFunction: PropTypes.func,
    openEditEventModal: PropTypes.func,
    matchedEventTypeForAsset: PropTypes.object,
    expanded: PropTypes.bool,
    onEventCollapseToggle: PropTypes.func
};